import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { useCompanyEnrichment } from "@/hooks/useCompanyEnrichment";
import { getContactCount, getDataScore } from "@/utils/company-scoring";
import { CompanyDTO } from "@shaarp/common";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  EyeNoneIcon,
} from "@radix-ui/react-icons";
import { Column, ColumnDef, Row } from "@tanstack/react-table";
import { InfoIcon, SparklesIcon, TriangleAlertIcon } from "lucide-react";
import { NavLink } from "react-router-dom";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import AvatarCompany from "../company/avatar-company";
import CompanyListingsSelector from "../company/company-listings-selector";
import CompanyNote from "../company/company-note";
import CompanyTagSelector from "../company/company-tag-selector";
import CompanyUserAssignedSelector from "../company/company-user-assigned-selector";
import CompanyIntegrationsSync from "../integrations/company/company-integrations-sync";
import { Badge } from "../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { DataTableCellCompanyAiReason } from "./data-table-cell-company-ai-reason";
import DataTableCellCompanyContact from "./data-table-cell-company-contact";
import { CompanyEnrichingIndicator } from "./data-table-cell-company-enriching";
import { DataTableCellCompanyEnrichment } from "./data-table-cell-company-enrichment";
import DataTableCellCompanyKeyContacts from "./data-table-cell-company-key-contacts";
import { CompanyInlineLinks } from "./data-table-cell-company-inline-links";
import { NotEnrichedIndicator } from "./data-table-cell-company-not-enriched";
import DataTableCellCompanySidePanel from "./data-table-cell-company-side-panel";
import {
  HEADCOUNT_RANGE_ORDER,
  REVENUE_RANGE_ORDER,
  translateActivityType,
} from "./data-table-companies-utils";

type ColumnDefWithVisibility = ColumnDef<CompanyDTO> & {
  initialVisibility?: boolean;
};

function CompanyNameCell({
  company,
  t,
}: {
  company: CompanyDTO;
  t: (key: string, opts?: Record<string, unknown>) => string;
}): React.ReactElement {
  const { latestData } = useCompanyEnrichment(company.id);
  const effectiveCompany = latestData ?? company;
  const description =
    effectiveCompany.activityMain &&
    effectiveCompany.activityMain !== effectiveCompany.activityTypeMain
      ? effectiveCompany.activityMain
      : effectiveCompany.business?.nafLabel ?? null;

  return (
    <div className="flex flex-col gap-1 min-w-[250px]">
      <div className="flex items-center gap-1.5">
        <NavLink
          to={`/company/${effectiveCompany.id}`}
          className="space-x-1"
          target="_blank"
        >
          <span className="font-bold">{effectiveCompany.name}</span>
        </NavLink>
        {description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="size-3.5 shrink-0 cursor-help text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent side="right" className="w-72 whitespace-pre-wrap">
                {description}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <CompanyInlineLinks company={effectiveCompany} t={t} />
    </div>
  );
}

export const dataTableCompaniesColumns = (
  additionalColumns: ColumnDef<CompanyDTO>[] = [],
  overrideColumnsVisibility: string[] = [],
  onRowChanged: (row: CompanyDTO) => void = () => {},
  t: (key: string, opts?: Record<string, unknown>) => string,
  showIntegrationsColumn: boolean = false,
  isAdminMode: boolean = false,
  showAssignedColumn: boolean = true,
  /** False for a team whose direct contact is cut: it sees no key contacts (#757). */
  showKeyContactsColumn: boolean = true,
): ColumnDefWithVisibility[] => {
  const columns = [
    // ── Enrichment ──
    {
      id: "Enrichment",
      accessorFn: (company: CompanyDTO) => company.enrichmentLevel,
      enableSorting: true,
      enableHiding: true,
      header: ({ column }) => {
        const sorted = column.getIsSorted();
        const label = t("components.dataTableCompanies.columns.enrichment");
        return (
          <div className="flex justify-center">
            <TooltipProvider>
              <Tooltip>
                <DropdownMenu>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 data-[state=open]:bg-accent"
                        aria-label={label}
                      >
                        {sorted === "desc" ? (
                          <ArrowDownIcon className="w-4 h-4" />
                        ) : sorted === "asc" ? (
                          <ArrowUpIcon className="w-4 h-4" />
                        ) : (
                          <SparklesIcon className="w-4 h-4" />
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem
                      onClick={() => column.toggleSorting(false)}
                    >
                      <ArrowUpIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                      Croissant
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => column.toggleSorting(true)}
                    >
                      <ArrowDownIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                      Décroissant
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => column.toggleVisibility(false)}
                    >
                      <EyeNoneIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                      Cacher
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <TooltipContent side="top">{label}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      cell: ({ row }) => {
        const company: CompanyDTO = row.original;
        if (isAdminMode) {
          return (
            <div className="flex justify-center">
              <DataTableCellCompanyEnrichment company={company} />
            </div>
          );
        }
        return (
          <div className="flex justify-center">
            <CompanyEnrichingIndicator
              t={t}
              enrichmentLevel={company.enrichmentLevel}
              companyId={company.id}
            />
          </div>
        );
      },
    },

    // ── AI warning icon ──
    {
      id: "ai-warning",
      enableSorting: false,
      enableHiding: false,
      size: 40,
      maxSize: 40,
      header: () => <span />,
      cell: ({ row }) => {
        const company: CompanyDTO = row.original;
        if (!company.isAiEstimated) return null;
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <TriangleAlertIcon className="w-4 h-4 text-warning" />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                {t("components.dataTableCompanies.columns.aiWarningTooltip")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },

    // ── Avatar ──
    {
      accessorKey: "avatar",
      header: "",
      cell: ({ row }) => {
        const company: CompanyDTO = row.original;
        return <AvatarCompany className="w-12 h-12" company={company} />;
      },
      enableSorting: false,
      enableHiding: false,
    },

    // ── Nom + liens inline ──
    {
      id: "Nom",
      accessorKey: "name",
      minSize: 250,
      sortingFn: (rowA, rowB) => {
        const a: string | null | undefined = rowA.original.name;
        const b: string | null | undefined = rowB.original.name;

        const isEmpty = (val: string | null | undefined) =>
          val === null || val === undefined || val.trim() === "";

        const aIsEmpty = isEmpty(a);
        const bIsEmpty = isEmpty(b);

        if (aIsEmpty && bIsEmpty) return 0;
        if (aIsEmpty) return 1;
        if (bIsEmpty) return -1;

        return a.localeCompare(b, undefined, { sensitivity: "base" });
      },
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanies.columns.name")}
        />
      ),
      cell: ({ row }) => {
        return <CompanyNameCell company={row.original} t={t} />;
      },
    },

    // ── Correspondance IA ──
    {
      id: "Correspondance IA",
      initialVisibility: false,
      enableHiding: false,
      accessorKey: "aiSearchReason",
      enableSorting: false,
      minSize: 180,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanies.columns.aiReason")}
        />
      ),
      cell: ({ row }) => {
        const company: CompanyDTO = row.original;
        return <DataTableCellCompanyAiReason company={company} />;
      },
    },

    // ── Code NAF/APE (hidden by default) ──
    {
      id: "Code NAF/APE",
      initialVisibility: false,
      accessorFn: (company: CompanyDTO) => company.business?.nafCode ?? "",
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.business?.nafCode ?? "";
        const b = rowB.original.business?.nafCode ?? "";
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return a.localeCompare(b);
      },
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanies.columns.nafCode")}
        />
      ),
      cell: ({ row }) => {
        const company: CompanyDTO = row.original;
        const nafCode = company.business?.nafCode;

        if (!nafCode && company.enrichmentLevel < 4) {
          return <NotEnrichedIndicator t={t} />;
        }

        return nafCode || "";
      },
    },

    // ── Adresse ──
    {
      id: "Adresse",
      accessorFn: (company: CompanyDTO) =>
        [company.location.address?.city, company.location.address?.zipCode]
          .filter(Boolean)
          .join(" "),
      sortingFn: (rowA, rowB) => {
        const cityA = rowA.original.location.address?.city ?? "";
        const cityB = rowB.original.location.address?.city ?? "";
        if (!cityA && !cityB) return 0;
        if (!cityA) return 1;
        if (!cityB) return -1;
        const cityCompare = cityA.localeCompare(cityB, undefined, {
          sensitivity: "base",
        });
        if (cityCompare !== 0) return cityCompare;
        const zipA = rowA.original.location.address?.zipCode ?? "";
        const zipB = rowB.original.location.address?.zipCode ?? "";
        return zipA.localeCompare(zipB);
      },
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanies.columns.address")}
        />
      ),
      cell: ({ row }) => {
        const company: CompanyDTO = row.original;

        return (
          <div className="w-full text-left">
            <p>{company.location.address?.line1}</p>
            <p>
              {company.location.address?.zipCode}{" "}
              {company.location.address?.city}
            </p>
          </div>
        );
      },
    },

    // ── Type d'activité (renamed) ──
    {
      id: "Type d'activité",
      accessorFn: (company: CompanyDTO) => company.activityTypeMain,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.activityTypeMain ?? "";
        const b = rowB.original.activityTypeMain ?? "";
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return a.localeCompare(b, undefined, { sensitivity: "base" });
      },
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanies.columns.activityType")}
        />
      ),
      cell: ({ row }) => {
        const company: CompanyDTO = row.original;
        const activityType = company.activityTypeMain;

        if (!activityType && company.enrichmentLevel < 4) {
          return <NotEnrichedIndicator t={t} />;
        }

        if (!activityType) return null;

        return (
          <Badge variant="secondary">
            {translateActivityType(t, activityType)}
          </Badge>
        );
      },
    },

    // ── Contact (moved after Activité) ──
    {
      id: "Contact",
      accessorFn: (company: CompanyDTO) => getContactCount(company),
      sortingFn: "basic",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanies.columns.contact")}
        />
      ),
      cell: ({ row }) => {
        const company: CompanyDTO = row.original;
        const contactCount = getContactCount(company);

        if (contactCount === 0) {
          return <NotEnrichedIndicator t={t} />;
        }

        return <DataTableCellCompanyContact company={company} />;
      },
    },

    // ── Contacts clés ──
    ...(showKeyContactsColumn
      ? [
          {
            id: "Contacts clés",
            enableSorting: false,
            header: ({ column }: { column: Column<CompanyDTO> }) => (
              <DataTableColumnHeader
                column={column}
                title={t("components.dataTableCompanies.columns.keyContacts")}
              />
            ),
            cell: ({ row }: { row: Row<CompanyDTO> }) => {
              const company: CompanyDTO = row.original;
              return <DataTableCellCompanyKeyContacts company={company} />;
            },
          },
        ]
      : []),

    // ── Chiffre d'affaires (with enrichment indicator) ──
    {
      id: "Chiffre d'affaires",
      accessorFn: (company: CompanyDTO) => company.estimatedRevenue?.range,
      sortingFn: (rowA, rowB) => {
        const a = REVENUE_RANGE_ORDER[rowA.original.estimatedRevenue?.range ?? ""] ?? 0;
        const b = REVENUE_RANGE_ORDER[rowB.original.estimatedRevenue?.range ?? ""] ?? 0;
        return a - b;
      },
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanies.columns.revenue")}
        />
      ),
      cell: ({ row }) => {
        const company: CompanyDTO = row.original;
        const estimatedRange = company.estimatedRevenue?.range;

        if (!estimatedRange) {
          return <NotEnrichedIndicator t={t} enrichmentLevel={company.enrichmentLevel} />;
        }

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="secondary" className="border border-dashed border-muted-foreground/30">
                  ~ {estimatedRange}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                {t("components.dataTableCompanies.columns.estimatedTooltip")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },

    // ── Effectif (with enrichment indicator) ──
    {
      id: "Effectif",
      accessorFn: (company: CompanyDTO) =>
        company.employeesCount?.estimatedRange,
      sortingFn: (rowA, rowB) => {
        const a = HEADCOUNT_RANGE_ORDER[rowA.original.employeesCount?.estimatedRange ?? ""] ?? 0;
        const b = HEADCOUNT_RANGE_ORDER[rowB.original.employeesCount?.estimatedRange ?? ""] ?? 0;
        return a - b;
      },
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanies.columns.employeesCount")}
        />
      ),
      cell: ({ row }) => {
        const company: CompanyDTO = row.original;
        const estimatedRange = company.employeesCount?.estimatedRange;

        if (!estimatedRange) {
          return <NotEnrichedIndicator t={t} enrichmentLevel={company.enrichmentLevel} />;
        }

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="secondary" className="border border-dashed border-muted-foreground/30">
                  ~ {estimatedRange}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                {t("components.dataTableCompanies.columns.estimatedTooltip")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },

    // ── SIRET (moved after Effectif, with enrichment indicator) ──
    {
      id: "SIRET",
      initialVisibility: false,
      accessorFn: (company: CompanyDTO) => company.business?.siretFormatted ?? "",
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.business?.siretFormatted ?? "";
        const b = rowB.original.business?.siretFormatted ?? "";
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return a.localeCompare(b);
      },
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanies.columns.siret")}
        />
      ),
      cell: ({ row }) => {
        const company: CompanyDTO = row.original;
        const siret = company.business?.siretFormatted;

        if (!siret && company.enrichmentLevel < 4) {
          return <NotEnrichedIndicator t={t} />;
        }

        return siret || "";
      },
    },

    // ── Data score (hidden, for sorting) ──
    {
      id: "data-score",
      initialVisibility: false,
      enableHiding: false,
      accessorFn: (company: CompanyDTO) => getDataScore(company),
      header: () => <span />,
      cell: () => null,
    },

    // ── Tag ──
    {
      id: "Tag",
      initialVisibility: false,
      accessorFn: (company: CompanyDTO) => company?.assignation?.tag,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanies.columns.tag")}
        />
      ),
      cell: ({ row }) => {
        const company: CompanyDTO = row.original;
        return <CompanyTagSelector company={company} />;
      },
      filterFn: (row, id, value) => {
        return !!value.includes(row.getValue(id));
      },
    },

    // ── Listes ──
    {
      id: "Listes",
      accessorFn: (company: CompanyDTO) => company.listingsIds?.length ?? 0,
      sortingFn: "basic",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanies.columns.lists")}
        />
      ),
      cell: ({ row }) => {
        const company: CompanyDTO = row.original;
        return <CompanyListingsSelector company={company} />;
      },
      filterFn: (row, id, value) => {
        return !!value.includes(row.getValue(id));
      },
    },

    // ── Assignation ──
    ...(showAssignedColumn
      ? [
          {
            id: "Assignation",
            initialVisibility: false,
            accessorFn: (company: CompanyDTO) => company?.assignation?.userId,
            header: ({ column }: { column: Column<CompanyDTO> }) => (
              <DataTableColumnHeader
                column={column}
                title={t("components.dataTableCompanies.columns.assignedTo")}
              />
            ),
            cell: ({ row }: { row: Row<CompanyDTO> }) => {
              const company: CompanyDTO = row.original;
              const onChange = (newUserId: string | null) => {
                row.original.assignation = {
                  ...row.original.assignation,
                  userId: newUserId ? newUserId : undefined,
                };
                onRowChanged(row.original);
              };
              return (
                <div className="flex justify-center">
                  <CompanyUserAssignedSelector company={company} onChange={onChange} />
                </div>
              );
            },
            filterFn: (row: Row<CompanyDTO>, id: string, value: string[]) => {
              return value.includes(row.getValue<string>(id));
            },
          },
        ]
      : []),

    // ── Notes ──
    {
      id: "Notes",
      initialVisibility: false,
      accessorKey: "notes",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanies.columns.notes")}
        />
      ),
      cell: ({ row }) => {
        const company: CompanyDTO = row.original;
        return <CompanyNote company={company} />;
      },
      filterFn: (row, id, value) => {
        return !!value.includes(row.getValue(id));
      },
    },

    // ── Integrations (conditional) ──
    ...(showIntegrationsColumn
      ? [
          {
            id: "Integrations",
            accessorKey: "integrations",
            header: ({ column }: { column: Column<CompanyDTO> }) => (
              <DataTableColumnHeader
                column={column}
                title={t(
                  "components.dataTableCompanies.columns.integrations"
                )}
              />
            ),
            cell: ({ row }: { row: Row<CompanyDTO> }) => {
              const company = row.original;
              return <CompanyIntegrationsSync company={company} />;
            },
          },
        ]
      : []),

    // ── Additional columns ──
    ...additionalColumns.map(
       
      (newColumn: any) =>
        ({
          id: newColumn[0],
          accessorFn: (company: CompanyDTO) =>
            typeof newColumn[1] === "function"
              ? newColumn[1](company)
              : company[newColumn[1] as keyof CompanyDTO],
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title={newColumn[0]} />
          ),
          cell: ({ row }) => {
            const company = row.original;
            return (
              <p>
                {typeof newColumn[1] === "function"
                  ? newColumn[1](company)
                  : company[newColumn[1] as keyof CompanyDTO]}
              </p>
            );
          },
        } as ColumnDef<CompanyDTO>)
    ),

    // ── Actions (sticky right) ──
    {
      id: "actions",
      enableHiding: false,
      enableSorting: false,
      cell: ({ row, table }) => (
        <DataTableCellCompanySidePanel
          company={row.original}
          getCompanies={() =>
            table.getSortedRowModel().rows.map((r) => r.original)
          }
        />
      ),
    },
  ] as ColumnDefWithVisibility[];

  if (overrideColumnsVisibility.length > 0) {
    overrideColumnsVisibility.forEach((columnId) => {
      const column = columns.find((c) => c.id === columnId);
      if (column) {
        column.initialVisibility = true;
      }
    });
  }

  return columns;
};
